import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import {
  ArrowDownTrayIcon,
  ArrowPathIcon,
  ChevronDownIcon,
  ChevronUpDownIcon,
  ChevronUpIcon,
  DocumentChartBarIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/outline';
import { Pagination } from '../../components/ui/Pagination';
import {
  badgeClass,
  formatMoney,
  formatNumber,
  getApiErrorMessage,
  inputClass,
  primaryButtonClass,
  secondaryButtonClass,
} from '../../components/ui/FormControls';
import { useReport, useReportCatalogue } from '../../hooks/useReports';
import { reportsService, ReportColumn, ReportFilters, ReportSummary } from '../../services/reports.service';
import { cn } from '../../lib/utils';

type PeriodKey = 'month' | 'lastMonth' | 'quarter' | 'year' | 'all' | 'custom';

const PERIODS: { key: PeriodKey; label: string }[] = [
  { key: 'month', label: 'This month' },
  { key: 'lastMonth', label: 'Last month' },
  { key: 'quarter', label: 'This quarter' },
  { key: 'year', label: 'This financial year' },
  { key: 'all', label: 'All time' },
  { key: 'custom', label: 'Custom' },
];

const PAGE_SIZES = [10, 25, 50, 100];

const thClass = 'px-4 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500 whitespace-nowrap';

const toInputDate = (date: Date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

// First and last day of a preset period. The financial year runs April to March.
const periodRange = (period: PeriodKey): { from: string; to: string } => {
  const today = new Date();
  const year = today.getFullYear();
  const month = today.getMonth();
  switch (period) {
    case 'month':
      return { from: toInputDate(new Date(year, month, 1)), to: toInputDate(new Date(year, month + 1, 0)) };
    case 'lastMonth':
      return { from: toInputDate(new Date(year, month - 1, 1)), to: toInputDate(new Date(year, month, 0)) };
    case 'quarter': {
      const start = Math.floor(month / 3) * 3;
      return { from: toInputDate(new Date(year, start, 1)), to: toInputDate(new Date(year, start + 3, 0)) };
    }
    case 'year': {
      const startYear = month >= 3 ? year : year - 1;
      return { from: toInputDate(new Date(startYear, 3, 1)), to: toInputDate(new Date(startYear + 1, 3, 0)) };
    }
    default:
      return { from: '', to: '' };
  }
};

const formatDate = (value: string) =>
  new Date(value).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });

const isNumeric = (column: ReportColumn) => ['money', 'number', 'percent'].includes(column.type);

const STATUS_TONES: Record<string, string> = {
  green: 'bg-green-50 text-green-700 ring-green-600/20',
  blue: 'bg-blue-50 text-blue-700 ring-blue-600/20',
  amber: 'bg-amber-50 text-amber-700 ring-amber-600/20',
  red: 'bg-red-50 text-red-700 ring-red-600/20',
  gray: 'bg-gray-50 text-gray-600 ring-gray-500/20',
};

const STATUS_TONE_BY_VALUE: Record<string, keyof typeof STATUS_TONES> = {
  delivered: 'green',
  completed: 'green',
  paid: 'green',
  received: 'green',
  ok: 'green',
  in: 'green',
  not_due: 'green',
  order: 'blue',
  confirmed: 'blue',
  shipped: 'blue',
  approved: 'blue',
  issued: 'blue',
  planned: 'blue',
  in_progress: 'blue',
  low: 'amber',
  overstocked: 'amber',
  on_hold: 'amber',
  out: 'amber',
  days_1_30: 'amber',
  days_31_60: 'amber',
  overdue: 'red',
  out_of_stock: 'red',
  cancelled: 'red',
  days_61_90: 'red',
  days_over_90: 'red',
};

const formatValue = (column: ReportColumn, value: string | number | null) => {
  if (value === null || value === undefined || value === '') return '—';
  switch (column.type) {
    case 'money':
      return formatMoney(Number(value));
    case 'number':
      return formatNumber(Number(value));
    case 'percent':
      return `${formatNumber(Number(value), 1)}%`;
    case 'date':
      return formatDate(String(value));
    default:
      return String(value);
  }
};

const Reports: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: catalogue = [], isLoading: catalogueLoading, isError: catalogueFailed, refetch: refetchCatalogue } = useReportCatalogue();

  const [catalogueSearch, setCatalogueSearch] = useState('');
  const [period, setPeriod] = useState<PeriodKey>('year');
  const [fromDate, setFromDate] = useState(() => periodRange('year').from);
  const [toDate, setToDate] = useState(() => periodRange('year').to);
  const [status, setStatus] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<{ column: string; direction: 'ASC' | 'DESC' } | null>(null);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [exporting, setExporting] = useState(false);

  // The report shown is kept in the address, so a report can be bookmarked or shared as a link
  const requestedKey = searchParams.get('report');
  const selected: ReportSummary | undefined = catalogue.find((report) => report.key === requestedKey) ?? catalogue[0];
  const selectedKey = selected?.key ?? null;

  const selectReport = (key: string) => {
    if (key === selectedKey) return;
    setSearchParams({ report: key }, { replace: true });
  };

  // Status, search and sorting belong to one report; the period carries over to the next
  useEffect(() => {
    setStatus('');
    setSearchInput('');
    setSearch('');
    setSort(null);
    setPage(1);
  }, [selectedKey]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const hasPeriod = !!selected?.periodLabel;
  const datesInOrder = !fromDate || !toDate || fromDate <= toDate;

  const filters: ReportFilters = useMemo(
    () => ({
      fromDate: hasPeriod && fromDate ? fromDate : undefined,
      toDate: hasPeriod && toDate ? toDate : undefined,
      status: status || undefined,
      search: search || undefined,
      sortBy: sort?.column,
      sortOrder: sort?.direction,
    }),
    [hasPeriod, fromDate, toDate, status, search, sort],
  );

  const { data, isLoading, isFetching, isError, error, refetch } = useReport(datesInOrder ? selectedKey : null, {
    ...filters,
    page,
    limit,
  });

  // Rows kept on screen while the next report loads belong to the previous one
  const result = data && data.report.key === selectedKey ? data : undefined;

  // Step back when the last page no longer exists
  useEffect(() => {
    if (result && page > result.totalPages) setPage(result.totalPages);
  }, [result, page]);

  const groups = useMemo(() => {
    const term = catalogueSearch.trim().toLowerCase();
    const matching = catalogue.filter(
      (report) =>
        !term ||
        report.name.toLowerCase().includes(term) ||
        report.description.toLowerCase().includes(term) ||
        report.category.toLowerCase().includes(term),
    );
    const byCategory = new Map<string, ReportSummary[]>();
    matching.forEach((report) => byCategory.set(report.category, [...(byCategory.get(report.category) ?? []), report]));
    return Array.from(byCategory.entries());
  }, [catalogue, catalogueSearch]);

  const choosePeriod = (key: PeriodKey) => {
    setPeriod(key);
    if (key !== 'custom') {
      const range = periodRange(key);
      setFromDate(range.from);
      setToDate(range.to);
    }
    setPage(1);
  };

  const changeDate = (side: 'from' | 'to', value: string) => {
    if (side === 'from') setFromDate(value);
    else setToDate(value);
    setPeriod('custom');
    setPage(1);
  };

  const sortColumn = sort?.column ?? selected?.defaultSort.column;
  const sortDirection = sort?.direction ?? selected?.defaultSort.direction;

  // First click on a number or a date shows the largest or latest first; text starts at A
  const toggleSort = (column: ReportColumn) => {
    if (sortColumn === column.key) {
      setSort({ column: column.key, direction: sortDirection === 'ASC' ? 'DESC' : 'ASC' });
    } else {
      setSort({ column: column.key, direction: isNumeric(column) || column.type === 'date' ? 'DESC' : 'ASC' });
    }
    setPage(1);
  };

  const hasFilters = !!status || !!searchInput || !!sort;
  const clearFilters = () => {
    setStatus('');
    setSearchInput('');
    setSearch('');
    setSort(null);
    setPage(1);
  };

  const handleExport = async () => {
    if (!selected) return;
    setExporting(true);
    try {
      const blob = await reportsService.exportReport(selected.key, filters);
      const stamp = filters.fromDate || filters.toDate ? `${filters.fromDate ?? 'start'}_${filters.toDate ?? toInputDate(new Date())}` : toInputDate(new Date());
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${selected.key}_${stamp}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`${selected.name} downloaded`);
    } catch (exportError) {
      toast.error(getApiErrorMessage(exportError, 'The report could not be downloaded'));
    } finally {
      setExporting(false);
    }
  };

  const statusLabels = useMemo(
    () => new Map((selected?.statuses ?? []).map((option) => [option.value, option.label])),
    [selected],
  );

  const renderCell = (column: ReportColumn, value: string | number | null) => {
    if (value === null || value === undefined || value === '') return <span className="text-gray-400">—</span>;
    if (column.type === 'status') {
      const key = String(value);
      return (
        <span className={cn(badgeClass, STATUS_TONES[STATUS_TONE_BY_VALUE[key] ?? 'gray'])}>
          {statusLabels.get(key) ?? key.replace(/_/g, ' ')}
        </span>
      );
    }
    if (column.type === 'code') {
      return <span className="whitespace-nowrap rounded bg-gray-100 px-1.5 py-0.5 font-mono text-xs text-gray-700">{String(value)}</span>;
    }
    return formatValue(column, value);
  };

  const totalColumns = selected?.columns.filter((column) => column.total) ?? [];
  const firstRow = result && result.total > 0 ? (result.page - 1) * result.limit + 1 : 0;
  const lastRow = result ? Math.min(result.page * result.limit, result.total) : 0;

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Reports</h1>
          <p className="mt-1 text-sm text-gray-500">
            Registers and summaries for sales, purchases, stock, invoices and production. Filter, sort, and download any of them.
          </p>
        </div>
        <button type="button" onClick={handleExport} disabled={!result || result.total === 0 || exporting} className={primaryButtonClass}>
          <ArrowDownTrayIcon className="-ml-0.5 mr-2 h-4 w-4" />
          {exporting ? 'Preparing…' : 'Export CSV'}
        </button>
      </div>

      {catalogueFailed ? (
        <div className="rounded-lg bg-white p-10 text-center shadow-sm ring-1 ring-gray-200">
          <p className="text-sm text-gray-700">The list of reports could not be loaded.</p>
          <button type="button" onClick={() => refetchCatalogue()} className={cn(secondaryButtonClass, 'mt-4')}>
            Try again
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-[17rem_minmax(0,1fr)]">
          {/* Catalogue */}
          <aside className="lg:sticky lg:top-4 lg:self-start">
            {/* A dropdown on small screens, the full list from large screens up */}
            <div className="lg:hidden">
              <label htmlFor="report-select" className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">
                Report
              </label>
              <select id="report-select" value={selectedKey ?? ''} onChange={(event) => selectReport(event.target.value)} className={inputClass}>
                {Array.from(new Set(catalogue.map((report) => report.category))).map((category) => (
                  <optgroup key={category} label={category}>
                    {catalogue
                      .filter((report) => report.category === category)
                      .map((report) => (
                        <option key={report.key} value={report.key}>
                          {report.name}
                        </option>
                      ))}
                  </optgroup>
                ))}
              </select>
            </div>

            <div className="hidden rounded-lg bg-white shadow-sm ring-1 ring-gray-200 lg:block">
              <div className="border-b border-gray-200 p-3">
                <div className="relative">
                  <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                  <input
                    type="search"
                    value={catalogueSearch}
                    onChange={(event) => setCatalogueSearch(event.target.value)}
                    placeholder="Find a report"
                    aria-label="Find a report"
                    className={cn(inputClass, 'pl-9')}
                  />
                </div>
              </div>
              <nav aria-label="Reports" className="max-h-[calc(100vh-14rem)] overflow-y-auto p-2">
                {catalogueLoading ? (
                  <div className="space-y-2 p-2">
                    {Array.from({ length: 8 }).map((_, index) => (
                      <div key={index} className="h-7 animate-pulse rounded bg-gray-100" />
                    ))}
                  </div>
                ) : groups.length === 0 ? (
                  <p className="p-3 text-sm text-gray-500">No report matches "{catalogueSearch.trim()}".</p>
                ) : (
                  groups.map(([category, reports]) => (
                    <div key={category} className="mb-2 last:mb-0">
                      <p className="px-2 pb-1 pt-2 text-xs font-semibold uppercase tracking-wide text-gray-400">{category}</p>
                      <ul>
                        {reports.map((report) => (
                          <li key={report.key}>
                            <button
                              type="button"
                              onClick={() => selectReport(report.key)}
                              aria-current={report.key === selectedKey ? 'page' : undefined}
                              className={cn(
                                'w-full rounded-md px-2 py-1.5 text-left text-sm',
                                report.key === selectedKey
                                  ? 'bg-indigo-50 font-semibold text-indigo-700'
                                  : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900',
                              )}
                            >
                              {report.name}
                            </button>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))
                )}
              </nav>
            </div>
          </aside>

          {/* Selected report */}
          <section className="min-w-0 space-y-4">
            {!selected ? (
              <div className="h-64 animate-pulse rounded-lg bg-white shadow-sm ring-1 ring-gray-200" />
            ) : (
              <>
                <div className="rounded-lg bg-white p-4 shadow-sm ring-1 ring-gray-200">
                  <div className="flex items-start gap-3">
                    <div className="hidden rounded-lg bg-indigo-50 p-2 sm:block">
                      <DocumentChartBarIcon className="h-5 w-5 text-indigo-600" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                        <h2 className="text-lg font-semibold text-gray-900">{selected.name}</h2>
                        <span className={cn(badgeClass, STATUS_TONES.gray)}>{selected.category}</span>
                      </div>
                      <p className="mt-0.5 text-sm text-gray-500">{selected.description}</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => refetch()}
                      disabled={isFetching}
                      title="Refresh"
                      aria-label="Refresh"
                      className="rounded-md p-2 text-gray-400 hover:bg-gray-50 hover:text-gray-600 disabled:opacity-50"
                    >
                      <ArrowPathIcon className={cn('h-5 w-5', isFetching && 'animate-spin')} />
                    </button>
                  </div>

                  {/* Filters */}
                  <div className="mt-4 space-y-3 border-t border-gray-100 pt-4">
                    {hasPeriod ? (
                      <div className="flex flex-col gap-3 xl:flex-row xl:items-end xl:justify-between">
                        <div>
                          <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500">{selected.periodLabel}</p>
                          <div className="inline-flex flex-wrap rounded-lg bg-gray-100 p-1">
                            {PERIODS.map((option) => (
                              <button
                                key={option.key}
                                type="button"
                                onClick={() => choosePeriod(option.key)}
                                aria-pressed={period === option.key}
                                className={cn(
                                  'rounded-md px-3 py-1.5 text-sm font-medium',
                                  period === option.key ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900',
                                )}
                              >
                                {option.label}
                              </button>
                            ))}
                          </div>
                        </div>
                        <div className="flex items-end gap-2">
                          <div>
                            <label htmlFor="report-from" className="mb-1 block text-xs font-medium text-gray-500">
                              From
                            </label>
                            <input
                              id="report-from"
                              type="date"
                              value={fromDate}
                              max={toDate || undefined}
                              onChange={(event) => changeDate('from', event.target.value)}
                              className={cn(inputClass, 'w-40')}
                            />
                          </div>
                          <div>
                            <label htmlFor="report-to" className="mb-1 block text-xs font-medium text-gray-500">
                              To
                            </label>
                            <input
                              id="report-to"
                              type="date"
                              value={toDate}
                              min={fromDate || undefined}
                              onChange={(event) => changeDate('to', event.target.value)}
                              className={cn(inputClass, 'w-40')}
                            />
                          </div>
                        </div>
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500">This report shows the position as of now, so it has no period.</p>
                    )}

                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                      {selected.searchable && (
                        <div className="relative flex-1">
                          <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                          <input
                            type="search"
                            value={searchInput}
                            onChange={(event) => setSearchInput(event.target.value)}
                            placeholder="Search this report"
                            aria-label="Search this report"
                            className={cn(inputClass, 'pl-9')}
                          />
                        </div>
                      )}
                      {selected.statuses.length > 0 && (
                        <select
                          value={status}
                          onChange={(event) => {
                            setStatus(event.target.value);
                            setPage(1);
                          }}
                          aria-label={selected.statusLabel ?? 'Status'}
                          className={cn(inputClass, 'sm:w-56')}
                        >
                          <option value="">{selected.statusLabel ?? 'Status'}: all</option>
                          {selected.statuses.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      )}
                      {hasFilters && (
                        <button type="button" onClick={clearFilters} className="text-sm font-medium text-indigo-600 hover:text-indigo-500">
                          Clear
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                {!datesInOrder ? (
                  <div className="rounded-lg bg-white p-10 text-center text-sm text-gray-700 shadow-sm ring-1 ring-gray-200">
                    The "to" date is before the "from" date. Change one of them to see the report.
                  </div>
                ) : isError ? (
                  <div className="rounded-lg bg-white p-10 text-center shadow-sm ring-1 ring-gray-200">
                    <p className="text-sm text-gray-700">{getApiErrorMessage(error, 'The report could not be loaded.')}</p>
                    <button type="button" onClick={() => refetch()} className={cn(secondaryButtonClass, 'mt-4')}>
                      Try again
                    </button>
                  </div>
                ) : (
                  <>
                    {/* Totals over the whole report */}
                    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-6">
                      <div className="rounded-lg bg-white p-4 shadow-sm ring-1 ring-gray-200">
                        <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Rows</p>
                        <p className="mt-1 text-xl font-semibold text-gray-900">{result ? formatNumber(result.total) : '—'}</p>
                      </div>
                      {totalColumns.map((column) => (
                        <div key={column.key} className="rounded-lg bg-white p-4 shadow-sm ring-1 ring-gray-200">
                          <p className="truncate text-xs font-medium uppercase tracking-wide text-gray-500" title={column.label}>
                            {column.label}
                          </p>
                          <p className="mt-1 truncate text-xl font-semibold text-gray-900">
                            {result ? formatValue(column, result.totals[column.key] ?? 0) : '—'}
                          </p>
                        </div>
                      ))}
                    </div>

                    <div className="overflow-hidden rounded-lg bg-white shadow-sm ring-1 ring-gray-200">
                      <div className={cn('overflow-x-auto', isFetching && result && 'opacity-60')}>
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              {selected.columns.map((column) => {
                                const active = sortColumn === column.key;
                                const SortIcon = !active ? ChevronUpDownIcon : sortDirection === 'ASC' ? ChevronUpIcon : ChevronDownIcon;
                                return (
                                  <th
                                    key={column.key}
                                    scope="col"
                                    aria-sort={active ? (sortDirection === 'ASC' ? 'ascending' : 'descending') : 'none'}
                                    className={cn(thClass, isNumeric(column) ? 'text-right' : 'text-left')}
                                  >
                                    <button
                                      type="button"
                                      onClick={() => toggleSort(column)}
                                      className={cn(
                                        'group inline-flex items-center gap-1 uppercase tracking-wide hover:text-gray-900',
                                        active && 'text-gray-900',
                                      )}
                                    >
                                      {column.label}
                                      <SortIcon className={cn('h-3.5 w-3.5', active ? 'text-indigo-600' : 'text-gray-300 group-hover:text-gray-400')} />
                                    </button>
                                  </th>
                                );
                              })}
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100">
                            {isLoading || !result ? (
                              Array.from({ length: 6 }).map((_, index) => (
                                <tr key={index}>
                                  {selected.columns.map((column) => (
                                    <td key={column.key} className="px-4 py-3">
                                      <div className="h-4 animate-pulse rounded bg-gray-100" />
                                    </td>
                                  ))}
                                </tr>
                              ))
                            ) : result.rows.length === 0 ? (
                              <tr>
                                <td colSpan={selected.columns.length} className="px-4 py-12 text-center">
                                  <p className="text-sm font-medium text-gray-900">Nothing to show</p>
                                  <p className="mt-1 text-sm text-gray-500">
                                    {status || search
                                      ? 'No rows match the search and filters.'
                                      : hasPeriod && (fromDate || toDate)
                                        ? 'There is nothing in this period. Try a longer one, or "All time".'
                                        : 'There is no data for this report yet.'}
                                  </p>
                                </td>
                              </tr>
                            ) : (
                              result.rows.map((row, index) => (
                                <tr key={`${result.page}-${index}`} className="hover:bg-gray-50">
                                  {selected.columns.map((column) => (
                                    <td
                                      key={column.key}
                                      className={cn(
                                        'px-4 py-3 text-sm text-gray-700',
                                        isNumeric(column) ? 'whitespace-nowrap text-right tabular-nums' : 'text-left',
                                        column.type === 'date' && 'whitespace-nowrap',
                                        column.type === 'money' && Number(row[column.key]) < 0 && 'text-red-600',
                                      )}
                                    >
                                      {renderCell(column, row[column.key])}
                                    </td>
                                  ))}
                                </tr>
                              ))
                            )}
                          </tbody>
                          {result && result.rows.length > 0 && totalColumns.length > 0 && (
                            <tfoot className="border-t-2 border-gray-200 bg-gray-50">
                              <tr>
                                {selected.columns.map((column, index) => (
                                  <td
                                    key={column.key}
                                    className={cn(
                                      'whitespace-nowrap px-4 py-3 text-sm font-semibold text-gray-900',
                                      isNumeric(column) ? 'text-right tabular-nums' : 'text-left',
                                    )}
                                  >
                                    {column.total
                                      ? formatValue(column, result.totals[column.key] ?? 0)
                                      : index === 0
                                        ? result.totalPages > 1
                                          ? `Total of all ${formatNumber(result.total)} rows`
                                          : 'Total'
                                        : ''}
                                  </td>
                                ))}
                              </tr>
                            </tfoot>
                          )}
                        </table>
                      </div>

                      <div className="flex flex-col gap-3 border-t border-gray-200 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
                        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-gray-600">
                          <span>
                            Showing {firstRow}–{lastRow} of {result ? formatNumber(result.total) : 0}
                          </span>
                          <label className="flex items-center gap-2">
                            Rows per page
                            <select
                              value={limit}
                              onChange={(event) => {
                                setLimit(Number(event.target.value));
                                setPage(1);
                              }}
                              className="rounded-md border border-gray-300 bg-white py-1 pl-2 pr-7 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                            >
                              {PAGE_SIZES.map((size) => (
                                <option key={size} value={size}>
                                  {size}
                                </option>
                              ))}
                            </select>
                          </label>
                        </div>
                        {result && result.totalPages > 1 && (
                          <Pagination currentPage={result.page} totalPages={result.totalPages} onPageChange={setPage} />
                        )}
                      </div>
                    </div>
                  </>
                )}
              </>
            )}
          </section>
        </div>
      )}
    </div>
  );
};

export default Reports;
